// src/pages/Devices.tsx
import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Key, X, AlertTriangle, Edit2, Trash2, Download, Wifi } from 'lucide-react';
import { devicesApi } from '../services/devicesApi';
import { Device, RegisterDeviceResponse, UpdateDeviceRequest } from '../types/devices';
import { Location } from '../types/locations';
import { locationsApi } from '../services/locationsApi';
import DeviceRegistrationForm from '../components/DeviceRegistrationForm';
import { useAuth } from '../contexts/AuthContext';
import PiConnectModal from '../components/PiConnectModal';
import { Settings } from 'lucide-react';  // Add to existing import
import { DeviceControlPanel } from '../components/DeviceControlPanel';
import '../components/DeviceControlPanel.css';
import StatusIndicator from '../components/StatusIndicator';

// Import the utility functions
import { getTimeAgo, formatDuration } from '../utils/dateUtils';

const Devices: React.FC = () => {
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showRegistrationForm, setShowRegistrationForm] = useState(false);
    const [showDeviceKey, setShowDeviceKey] = useState<RegisterDeviceResponse | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(undefined);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showPiConnectModal, setShowPiConnectModal] = useState(false);
    const [piConnectDevice, setPiConnectDevice] = useState<Device | null>(null);
    const [showControlPanel, setShowControlPanel] = useState(false);
    const [controlPanelDevice, setControlPanelDevice] = useState<Device | null>(null);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            const devicesData = await devicesApi.getAllDevices();
            setDevices(devicesData);
            setError(null);
        } catch (err) {
            setError('Failed to fetch devices');
            console.error('Error fetching devices:', err);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchDevices();
    }, []);

    const handleEditDevice = (device: Device) => {
        setSelectedDevice(device);
    };

    const handleDeleteDevice = async (deviceId: number) => {
        try {
            setIsDeleting(true);
            await devicesApi.deleteDevice(deviceId);
            await fetchDevices();
            setSuccessMessage('Device deleted successfully');
            setShowDeleteConfirm(null);
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError('Failed to delete device');
            console.error('Error deleting device:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleShowPiConnect = (device: Device) => {
        setPiConnectDevice(device);
        setShowPiConnectModal(true);
    };

    const formatBytes = (bytes?: number) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const handleControlDevice = (device: Device) => {
        setControlPanelDevice(device);
        setShowControlPanel(true);
    };

    return (
        <div>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">Devices</h1>
                    <p className="text-gray-600">Manage and monitor your display devices</p>
                </div>
                <div className="flex space-x-4">
                    <button
                        onClick={fetchDevices}
                        className="flex items-center px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowRegistrationForm(true)}
                        className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Device  {/* Changed from "Register Device" */}
                    </button>
                </div>                
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="p-4 mb-6 text-green-700 bg-green-100 rounded-lg">
                    {successMessage}
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="p-4 mb-6 text-red-700 bg-red-100 rounded-lg">
                    {error}
                </div>
            )}

            {/* Devices Table */}
           <div className="bg-white rounded-lg shadow">
                <div className="overflow-x-auto">
                    <div className="min-w-full align-middle">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Device
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Location
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Heartbeat
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Storage
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        System
                                    </th>
                                    <th scope="col" className="relative px-6 py-3">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                            Loading devices...
                                        </td>
                                    </tr>
                                ) : devices.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No devices found
                                        </td>
                                    </tr>
                                ) : (
                                    devices.map((device) => (
                                        <tr key={device.deviceId} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{device.deviceName}</div>
                                                <div className="text-sm text-gray-500">{device.ipAddress}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{device.location?.locationName || 'Unassigned'}</div>
                                                <div className="text-sm text-gray-500">{device.location?.city}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusIndicator status={device.status} reasons={device.statusReasons} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {getTimeAgo(device.lastHeartbeat) || 'Never'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {device.currentStatus && (
                                                    <div className="w-32">
                                                        <div className="text-xs text-gray-600 mb-1">Disk Usage</div>
                                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                                            <div 
                                                                className="bg-blue-600 h-2 rounded-full" 
                                                                style={{ width: `${device.currentStatus.diskUsage || 0}%` }}
                                                            />
                                                        </div>
                                                        <div className="text-xs text-gray-600 mt-1">
                                                            {device.currentStatus.diskUsage?.toFixed(1)}%
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {device.currentStatus && (
                                                    <div className="text-xs text-gray-500">
                                                        {device.currentStatus.cpuUsage && (
                                                            <div>CPU: {device.currentStatus.cpuUsage}%</div>
                                                        )}
                                                        {device.currentStatus.memoryUsage && (
                                                            <div>Memory: {device.currentStatus.memoryUsage}%</div>
                                                        )}
                                                        {device.currentStatus.temperature && (
                                                            <div>Temp: {device.currentStatus.temperature}°C</div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <button 
                                                        onClick={() => handleEditDevice(device)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                        title="Edit Device"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>                                                    
                                                    <button 
                                                        onClick={() => handleShowPiConnect(device)}
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Pi Connect URL"
                                                    >
                                                        <Wifi className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleControlDevice(device)}
                                                        className="text-purple-600 hover:text-purple-800"
                                                        title="Control Device"
                                                    >
                                                        <Settings className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => setShowDeleteConfirm(device.deviceId)}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Delete Device"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Device Registration Form */}
            <DeviceRegistrationForm
                isOpen={showRegistrationForm}
                onClose={() => setShowRegistrationForm(false)}
                onRegistered={() => {
                    setShowRegistrationForm(false);
                    fetchDevices();
                    setSuccessMessage('Device added successfully');
                    setTimeout(() => setSuccessMessage(null), 3000);
                }}
            />            

            {/* Edit Device Modal */}
            {selectedDevice && (
                <EditDeviceModal
                    device={selectedDevice}
                    onClose={() => setSelectedDevice(undefined)}
                    onUpdate={() => {
                        setSelectedDevice(undefined);
                        fetchDevices();
                        setSuccessMessage('Device updated successfully');
                        setTimeout(() => setSuccessMessage(null), 3000);
                    }}
                />
            )}

            {/* Pi Connect Modal */}
            {piConnectDevice && (
            <PiConnectModal
                isOpen={showPiConnectModal}
                onClose={() => {
                setShowPiConnectModal(false);
                setPiConnectDevice(null);
                }}
                deviceName={piConnectDevice.deviceName}
                deviceId={piConnectDevice.deviceId}  // Add this line
                piConnectUrl={piConnectDevice.piConnectUrl}
                onRequestPiConnect={async (deviceId) => {
                // Add your API call here
                await devicesApi.requestPiConnect(deviceId);
                }}
                onRefreshDevice={async (deviceId) => {
                // Add your API call here
                const updatedDevice = await devicesApi.refreshDevice(deviceId);
                return updatedDevice;
                }}
            />
            )}
            
            {showControlPanel && controlPanelDevice && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => { setShowControlPanel(false); setControlPanelDevice(null); }}>
                    <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}>
                        <DeviceControlPanel
                            deviceId={controlPanelDevice.deviceId}
                            deviceName={controlPanelDevice.deviceName}
                            onClose={() => { setShowControlPanel(false); setControlPanelDevice(null); }}
                        />
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
                        <h3 className="text-lg font-medium mb-4">Delete Device</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this device? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteDevice(showDeleteConfirm)}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Device Key Modal */}
            {showDeviceKey && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium flex items-center">
                                <Key className="w-5 h-5 mr-2 text-amber-500" />
                                Device Key
                            </h3>
                            <button onClick={() => setShowDeviceKey(null)} className="text-gray-400 hover:text-gray-500">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="bg-amber-50 p-4 rounded-md mb-4">
                            <div className="flex items-start">
                                <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
                                <p className="text-sm text-amber-800">
                                    This key will only be shown once. Please save it now - you'll need it to configure the device.
                                </p>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Device ID
                            </label>
                            <div className="bg-gray-50 px-3 py-2 rounded border">
                                {showDeviceKey.deviceId}
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Device Key
                            </label>
                            <div className="bg-gray-50 px-3 py-2 rounded border font-mono text-sm break-all">
                                {showDeviceKey.deviceToken}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowDeviceKey(null)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface EditDeviceModalProps {
    device: Device;
    onClose: () => void;
    onUpdate: () => void;
}

const EditDeviceModal: React.FC<EditDeviceModalProps> = ({ device, onClose, onUpdate }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<UpdateDeviceRequest>({
        deviceName: device.deviceName,
        serialNumber: device.serialNumber,
        locationId: device.locationId,
        active: device.active ?? true
    });
    const [locations, setLocations] = useState<Location[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Check if device is registered (has a real serial number)
    const isRegistered = device.serialNumber && device.serialNumber !== '' && !device.serialNumber.startsWith('PENDING');
    const isPending = device.status === 'Pending';

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const data = await locationsApi.getAllLocations();
            setLocations(data);
        } catch (err) {
            console.error('Error fetching locations:', err);
            setError('Failed to load locations');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.deviceName?.trim()) {
            setError('Device name is required');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            await devicesApi.updateDevice(device.deviceId, formData);
            onUpdate();
        } catch (err) {
            setError('Failed to update device: ' + (err instanceof Error ? err.message : String(err)));
        } finally {
            setSaving(false);
        }
    };

    const downloadConfigJson = () => {
        if (!device || !user) return;

        // Determine API endpoint based on current environment
        const currentUrl = window.location.origin;
        let apiEndpoint = 'https://api.pinage.co.uk';
        
        if (currentUrl.includes('localhost') || currentUrl.includes('127.0.0.1')) {
            apiEndpoint = 'http://localhost:5110'; // Development
        } else if (currentUrl.includes('easemylife.com')) {
            apiEndpoint = 'https://api.easemylife.com'; // Beta
        }

        // We need the device token - this would need to be included in the Device object
        // or fetched separately. For now, we'll show a message
        const config = {
            api_endpoint: apiEndpoint,
            api_key: 'pinage_system_2024_k7m8n9p0q1r2s3t4u5v6w7x8y9z0',
            device_token: 'DEVICE_TOKEN_HERE', // This needs to be retrieved
            company_id: user.companyId
        };

        const jsonString = JSON.stringify(config, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = 'firstrunPinage.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md">
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold">Edit Device</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Show status badge */}
                    <div className="mb-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            isPending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                            {isPending ? 'Pending Registration' : 'Registered'}
                        </span>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Device Name *
                            </label>
                            <input
                                type="text"
                                value={formData.deviceName || ''}
                                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                required
                            />
                        </div>

                        {/* Only show serial number if device is registered */}
                        {isRegistered && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Serial Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.serialNumber || ''}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
                                    disabled
                                    title="Serial number cannot be changed after registration"
                                />
                                <p className="mt-1 text-xs text-gray-500">Serial number is set by the device</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Location
                            </label>
                            <select
                                value={formData.locationId || ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData({
                                        ...formData,
                                        locationId: value ? parseInt(value) : undefined
                                    });
                                }}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            >
                                <option value="">No location assigned</option>
                                {locations.map((location) => (
                                    <option key={location.locationId} value={location.locationId}>
                                        {location.locationName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Show additional fields if registered */}
                        {isRegistered && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        IP Address
                                    </label>
                                    <input
                                        type="text"
                                        value={device.ipAddress || 'Not reported'}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Last Heartbeat
                                    </label>
                                    <input
                                        type="text"
                                        value={device.lastHeartbeat ? new Date(device.lastHeartbeat).toLocaleString() : 'Never'}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
                                        disabled
                                    />
                                </div>
                            </>
                        )}

                        <div>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.active ?? true}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Device is active</span>
                            </label>
                        </div>

                        {/* Show download config button if device is pending */}
                        {isPending && (
                            <div className="border-t pt-4">
                                <button
                                    type="button"
                                    onClick={downloadConfigJson}
                                    className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                >
                                    <Download className="w-5 h-5 mr-2" />
                                    Download firstrunPinage.json
                                </button>                               
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Devices;